import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { FormSheet, FormSheetPrimaryButton } from "@/components/ui/FormSheet";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { saveVisitCompanyComment, visitErrorKey } from "@/lib/planner/visitReportApi";

/**
 * El comentario de la EMPRESA sobre el informe de un perito (C3, 26-09-2026).
 *
 * Antes el admin solo tenía «Editar informe», que le dejaba escribir encima del
 * informe del perito (y lo borraba). Ahora el informe es del perito y la empresa
 * deja su comentario aparte, con su nombre y su fecha. Mismo formulario que el
 * resto de la app (FormSheet: X arriba a la derecha, confirmación si hay cambios).
 */
export function VisitCommentDialog({
  open,
  onOpenChange,
  taskId,
  initialText,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
  initialText?: string | null;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [text, setText] = useState(initialText ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setText(initialText ?? "");
  }, [open, initialText]);

  const save = async () => {
    setSaving(true);
    try {
      await saveVisitCompanyComment(taskId, text, user?.id);
      toast({ title: t("common.saved") });
      queryClient.invalidateQueries({ queryKey: ["visit-report"] });
      queryClient.invalidateQueries({ queryKey: ["admin-seguimiento"] });
      queryClient.invalidateQueries({ queryKey: ["perito-seguimiento"] });
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : (err as { message?: string } | null)?.message;
      const key = visitErrorKey(null, message);
      toast({ title: key ? t(key) : message || t("common.error"), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={t("perito.seguimiento.companyComment")}
      subtitle={t("perito.seguimiento.companyCommentHint")}
      isDirty={text !== (initialText ?? "")}
      footer={
        <FormSheetPrimaryButton onClick={save} disabled={saving}>
          {saving ? t("common.saving") : t("common.save")}
        </FormSheetPrimaryButton>
      }
    >
      <div className="space-y-2 py-2">
        <Label htmlFor="visit-company-comment">{t("perito.seguimiento.companyComment")}</Label>
        <Textarea
          id="visit-company-comment"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("perito.seguimiento.companyCommentPlaceholder")}
          rows={4}
        />
      </div>
    </FormSheet>
  );
}
